import React from "react";
import { createRoot } from "react-dom/client";
import html2canvas from "html2canvas";
import PdfDocument from "./PdfDocument";
import { inlineSignatureImage } from "./pdfHelpers";

// US Letter, points.
export const PAGE_WIDTH_PT = 612;
export const PAGE_HEIGHT_PT = 792;
export const MARGIN_X_PT = 48;
// Real header/footer band reserved on every physical page — a running
// header line + a "Page X of Y" footer, drawn separately from the captured
// image (crisp vector text in the real PDF, plain CSS text in the on-screen
// preview) so both use the same page boundaries.
export const HEADER_PT = 46;
export const FOOTER_PT = 40;
export const CONTENT_HEIGHT_PT = PAGE_HEIGHT_PT - HEADER_PT - FOOTER_PT;

// Renders PdfDocument offscreen and rasterizes it with html2canvas — shared
// by the real PDF download (pdfExport.js) and the on-screen page-by-page
// preview (PdfPreviewModal.jsx), so both are built from the exact same
// capture and always land on identical page boundaries.
export async function captureDocumentCanvas(draft) {
  // Must happen before the render: the capture can only draw a signature that
  // is already inline (see inlineSignatureImage).
  const source = await inlineSignatureImage(draft);

  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "0";
  container.style.left = "-99999px";
  container.style.zIndex = "-1";
  document.body.appendChild(container);

  const root = createRoot(container);
  try {
    await new Promise((resolve) => {
      root.render(React.createElement(PdfDocument, { draft: source, plain: true }));
      // Two rAF ticks: the first lets React commit the render, the second
      // lets the browser finish layout/paint before html2canvas reads it.
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    // Every image, not just the first: html2canvas reads the laid-out DOM, so
    // anything still in flight is captured as a blank box.
    await Promise.all(
      Array.from(container.querySelectorAll("img")).map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((resolve) => {
              img.onload = resolve;
              img.onerror = resolve;
            })
      )
    );

    return await html2canvas(container, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
    });
  } finally {
    root.unmount();
    document.body.removeChild(container);
  }
}

// Slices a captured canvas into page-sized chunks (contentHeightPt tall, at
// the canvas's own resolution) and returns them as data URLs — the same
// slicing math the real PDF export uses, so the preview always shows exactly
// the pages the download will produce.
export function sliceCanvasToPages(canvas, contentHeightPt) {
  const pxPerPt = canvas.width / PAGE_WIDTH_PT;
  const pageHeightPx = contentHeightPt * pxPerPt;
  const pages = [];
  let renderedPx = 0;
  while (renderedPx < canvas.height) {
    const sliceHeightPx = Math.min(pageHeightPx, canvas.height - renderedPx);
    const slice = document.createElement("canvas");
    slice.width = canvas.width;
    slice.height = sliceHeightPx;
    slice
      .getContext("2d")
      .drawImage(canvas, 0, renderedPx, canvas.width, sliceHeightPx, 0, 0, canvas.width, sliceHeightPx);
    pages.push({
      dataUrl: slice.toDataURL("image/png"),
      widthPt: PAGE_WIDTH_PT,
      heightPt: sliceHeightPx / pxPerPt,
    });
    renderedPx += sliceHeightPx;
  }
  return pages;
}
